import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeStatus, STATUS_COLORS } from '../../../../types/fingeringEditor';
import { PART_LABELS } from '../../../../constants/fingering';
import type { FingeringMode, FingeringPart } from '../../../../types/fingering';

interface EditorHeaderProps {
  title: string;
  part: string;
  /** issue #91 round2 (#44): part 表示の出し分け。 'part' は part 非表示、 'score' は (1st-4th)。 */
  mode?: FingeringMode;
  stats: Record<BadgeStatus, number>;
}

/**
 * issue #91 round2 (#44): part を生 (Guitar1) でなく PART_LABELS (1st-4th) に変換し mode で出し分ける。
 * - mode='part' (パート譜): part 非表示 (元々 1 パートなので冗長)。
 * - mode='score' (総譜): (1st-4th)。
 * - mode 欠落 (古い stored): 安全側で PART_LABELS 変換 (Guitar は出さない)。 未知 part は生フォールバック。
 */
function partDisplay(part: string, mode?: FingeringMode): string | null {
  if (mode === 'part') return null;
  return PART_LABELS[part as FingeringPart] ?? part;
}

/**
 * 運指エディタ画面のヘッダー (戻るリンク + タイトル + status カウントバッジ)。
 * FingeringEditorPage から issue #17 Phase 2 で抽出。
 * issue #26 Phase 7-1: Phase 1 の compact 化 (1 行集約) はユーザーフィードバックで撤回、
 *   ロゴ大きめ (w-8 h-8) + title/part 縦 2 段 (mt-0.5) の元レイアウトに復活。
 */
const EditorHeader: React.FC<EditorHeaderProps> = ({ title, part, mode, stats }) => {
  const partLabel = partDisplay(part, mode);
  return (
    <header className="px-4 py-3 bg-slate-800/90 backdrop-blur-sm border-b border-slate-700/60 flex justify-between items-center flex-shrink-0 z-10">
      <div className="flex items-center gap-2.5">
        <Link to="/fingering" className="text-slate-500 hover:text-slate-300 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <img src="/omakase-unshi-kun.png" alt="" className="w-8 h-8 brightness-125 drop-shadow-[0_0_6px_rgba(255,255,255,0.3)]" />
        <div>
          <h1 className="text-sm font-extrabold tracking-tight bg-gradient-to-r from-amber-400 via-orange-400 to-purple-400 bg-clip-text text-transparent">運指エディタ</h1>
          <div className="text-[10px] text-slate-500 mt-0.5">{title}{partLabel ? ` — ${partLabel}` : ''}</div>
        </div>
      </div>
      <div className="flex gap-1.5">
        {(Object.entries(stats) as [BadgeStatus, number][]).map(([k, v]) => v > 0 ? (
          <span key={k} data-testid="status-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
            style={{ background: STATUS_COLORS[k].bg + '22', color: STATUS_COLORS[k].bg }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLORS[k].bg }} />{v}
          </span>
        ) : null)}
      </div>
    </header>
  );
};

export default EditorHeader;
